import { FunctionComponent, h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useStore } from '../../helpers/use-store';
import { observer } from 'mobx-react';
import SharedLocationContent from './shared-location-content/share-location-content';
import { Layout, LayoutContent } from '@bringg/react-components';
import { applyThemeToCssVariables, getAppTheme, ThemeContext } from '../../hooks/use-theme/use-theme';
import Loading from '../../components/loading/loading';
import CustomerExperienceContent from './customer-experience-content/customer-experience-content';
import { ApiVersion, BaseCustomerExperienceData } from '@bringg/types';
import ErrorPage from '../../pages/error-page/error-page';
import { get as _get } from 'lodash';
import { SharedLocationStore } from '../../stores/shared-location/shared-location-store';
import { CustomerExperienceStore } from '../../stores/customer-experience/customer-experience-store';
import { BaseSharedLocationConfig } from '../../types/shared-location.types';

interface Props {
	shared_location_uuid: string;
}

const getCustomerExperienceContent = (
	sharedLocationStore: SharedLocationStore,
	customerExperienceStore: CustomerExperienceStore,
	apiVersion: ApiVersion,
	isError: boolean,
	isLoading: boolean
) => {
	if (isLoading) {
		return <Loading applyTheme={false} />;
	}

	if (isError) {
		return <ErrorPage />;
	}

	switch (apiVersion) {
		case ApiVersion.V1: {
			const { sharedLocationState, sharingType, allowCancelByEventType, displayScreen } = sharedLocationStore;
			return (
				<SharedLocationContent
					sharedLocationState={sharedLocationState}
					sharingType={sharingType}
					allowCancelByEventType={allowCancelByEventType}
					displayScreen={displayScreen}
				/>
			);
		}
		case ApiVersion.V2: {
			const customerExperience = customerExperienceStore.customerExperience as BaseCustomerExperienceData;
			return <CustomerExperienceContent taskPhase={customerExperience.task_phase} />;
		}
		default:
			return <ErrorPage />;
	}
};

export const CustomerExperience: FunctionComponent<Props> = observer(({ shared_location_uuid }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [theme, setTheme] = useState(getAppTheme({}));

	const rootStore = useStore();

	useEffect(() => {
		const init = async () => {
			const config = (await rootStore.initCustomerExperience(shared_location_uuid)) as BaseSharedLocationConfig;
			const ui_data = config?.ui_data;

			if (ui_data) {
				const headerColor = _get(ui_data, 'header_color');
				const textColor = _get(ui_data, 'eta_title_color');

				setTheme(
					getAppTheme({
						headerColor,
						primaryColor: headerColor,
						textColor,
						disableCxColorManipulation: config?.disable_cx_color_manipulation
					})
				);
			}

			setIsLoading(false);
		};

		init();
	}, [rootStore]);

	useEffect(() => {
		applyThemeToCssVariables(theme);
	}, [theme]);

	const { sharedLocationStore, customerExperienceStore, apiVersion, isError } = rootStore;

	return (
		<ThemeContext.Provider key={shared_location_uuid} value={theme}>
			<Layout className="customer-experience" key={shared_location_uuid}>
				<LayoutContent className="customer-experience-content-layout">
					{getCustomerExperienceContent(
						sharedLocationStore,
						customerExperienceStore,
						apiVersion,
						isError,
						isLoading
					)}
				</LayoutContent>
			</Layout>
		</ThemeContext.Provider>
	);
});
